import type { SupportAction, SupportBlock, SupportReply } from '../../api/help-content';
import { supportContact } from './help-content';
export type AgentAction = SupportAction;
export type AgentBlock = SupportBlock;
export type AgentReply = SupportReply;
export interface AgentContact {
  url: string;
  label: string;
}
export function supportContactUrl(contact?: AgentContact) {
  return contact ? (supportContact(contact.url)?.url ?? null) : null;
}
export interface AgentResult {
  conversation_id: string;
  message_id: string;
  status?: 'processing';
  reply?: AgentReply;
  no_answer?: boolean;
}
export interface AgentInput {
  conversation_id?: string;
  message: string;
}
export interface AgentApi {
  ask(body: AgentInput, key: string): Promise<AgentResult>;
  agentMessage(id: string, signal?: AbortSignal): Promise<AgentResult>;
  rate(body: { conversation_id: string; message_id: string; value: -1 | 0 | 1 }): Promise<unknown>;
}
export interface AgentTurn {
  requestId: string;
  input: AgentInput;
  phase: 'unknown' | 'processing' | 'done' | 'uncertain';
  response?: AgentResult;
  rating?: -1 | 0 | 1;
}
export interface AgentChat {
  owner: number;
  conversationId?: string;
  turns: AgentTurn[];
  archived: Array<{ requestId: string; conversationId?: string; messageId?: string; phase: AgentTurn['phase'] | 'rejected' }>;
}
export const agentGuid = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
export const agentText = (text: string) => Array.from(text).slice(0, 300).join('');
const chatKey = (owner: number) => `steos:help:agent:${owner}`;
export function readAgentChat(owner: number): AgentChat {
  const raw = sessionStorage.getItem(chatKey(owner));
  if (!raw) return { owner, turns: [], archived: [] };
  if (raw.length > 512 * 1024) throw new Error('agent_chat_invalid');
  const value = JSON.parse(raw) as AgentChat;
  if (
    !value ||
    value.owner !== owner ||
    !Array.isArray(value.turns) ||
    value.turns.length > 20 ||
    !Array.isArray(value.archived) ||
    value.archived.length > 40 ||
    (value.conversationId && !agentGuid(value.conversationId))
  )
    throw new Error('agent_chat_invalid');
  for (const turn of value.turns) {
    if (
      !turn ||
      !agentGuid(turn.requestId) ||
      !['unknown', 'processing', 'done', 'uncertain'].includes(turn.phase) ||
      typeof turn.input?.message !== 'string' ||
      !turn.input.message.trim() ||
      Array.from(turn.input.message).length > 300 ||
      (turn.input.conversation_id && !agentGuid(turn.input.conversation_id))
    )
      throw new Error('agent_chat_invalid');
    if (turn.response) validateAgentResult(turn.response);
    if ((turn.phase === 'processing' || turn.phase === 'done') && !turn.response) throw new Error('agent_chat_invalid');
    if (
      (turn.phase === 'done' && turn.response?.status === 'processing') ||
      (turn.phase === 'processing' && turn.response?.status !== 'processing') ||
      (turn.rating !== undefined && ![-1, 0, 1].includes(turn.rating))
    )
      throw new Error('agent_chat_invalid');
  }
  if (value.archived.some(turn => !turn || !agentGuid(turn.requestId) || !['unknown', 'processing', 'done', 'uncertain', 'rejected'].includes(turn.phase)))
    throw new Error('agent_chat_invalid');
  return value;
}
export function saveAgentChat(chat: AgentChat) {
  sessionStorage.setItem(chatKey(chat.owner), JSON.stringify(chat));
  document.dispatchEvent(new CustomEvent('steos:agent-updated', { detail: { owner: chat.owner } }));
}
export function validateAgentResult(value: AgentResult) {
  if (!agentGuid(value?.conversation_id) || !agentGuid(value?.message_id)) throw new Error('support_response_invalid');
  if (value.status !== undefined && value.status !== 'processing') throw new Error('support_response_invalid');
  if (value.status === 'processing') return;
  if (
    !value.reply ||
    !Array.isArray(value.reply.blocks) ||
    value.reply.blocks.length < 1 ||
    value.reply.blocks.length > 12 ||
    typeof value.no_answer !== 'boolean' ||
    !Array.isArray(value.reply.actions) ||
    !Array.isArray(value.reply.related)
  )
    throw new Error('support_response_invalid');
  if (JSON.stringify(value.reply).length > 20000) throw new Error('support_response_invalid');
  const texts = (values: unknown, max: number) =>
    Array.isArray(values) && values.length <= max && values.every(item => typeof item === 'string' && item.length <= 4000);
  for (const block of value.reply.blocks) {
    if (!block || typeof block !== 'object') throw new Error('support_response_invalid');
    const valid =
      block.type === 'p' || block.type === 'price'
        ? typeof block.text === 'string'
        : block.type === 'steps'
          ? texts(block.items, 20)
          : block.type === 'sections'
            ? Array.isArray(block.items) &&
              block.items.length <= 12 &&
              block.items.every(item => item && typeof item.title === 'string' && texts(item.rows, 20))
            : block.type === 'table'
              ? typeof block.title === 'string' &&
                texts(block.cols, 8) &&
                Array.isArray(block.rows) &&
                block.rows.length <= 30 &&
                block.rows.every(row => texts(row, 8) && row.length === block.cols.length)
              : block.type === 'refs'
                ? Array.isArray(block.items) &&
                  block.items.length <= 5 &&
                  block.items.every(item => item && ['faq', 'guide', 'telegram'].includes(item.kind) && typeof item.id === 'string')
                : false;
    if (!valid) throw new Error('support_response_invalid');
  }
  if (
    !texts(value.reply.related, 2) ||
    value.reply.actions.length > 5 ||
    value.reply.actions.some(
      action =>
        !action ||
        !['tool', 'collection', 'screen', 'sheet'].includes(action.kind) ||
        typeof action.id !== 'string' ||
        (action.title != null && typeof action.title !== 'string'),
    )
  )
    throw new Error('support_response_invalid');
}
// Один HTTP POST на намерение даже при закрытии и повторном открытии листа в той же странице.
const requests = new Map<string, Promise<AgentResult>>();
export function runAgentRequest(owner: number, turn: AgentTurn, api: AgentApi) {
  const key = `${owner}:${turn.requestId}`;
  const current = requests.get(key);
  if (current) return current;
  const promise = api.ask(turn.input, turn.requestId);
  requests.set(key, promise);
  void promise
    .finally(() => {
      if (requests.get(key) === promise) requests.delete(key);
    })
    .catch(() => {});
  return promise;
}
